import { GPT_MODELS, GPT_PROFILE_INIT_DATA } from "@/global/_constants";
import validations from "@/helpers/validations";
import _ from "lodash";

export class GptProfileMapper {

    static mapContentModeration(config = { categories: [] }) {
      if (!config || typeof config !== "object") return {
        is_filtering_enabled: false,
        categories: []
      };

      return {
        is_filtering_enabled: config.is_filtering_enabled ?? false,
        categories: (config.categories || []).map(cat => ({
          name: cat.name || "",
          keywords: cat.keywords || [],
          detection_rules: cat.detection_rules || [],
          severity_actions: cat.severity_actions || {},
          termination_message: cat.termination_message || null
        }))
      };
    }

    static mapExternalSystemConfig(configs = []) {
      if (!Array.isArray(configs)) return [];

      return configs.map(config => ({
        name: config.name || "",
        redirection_message: config.redirection_message || null,
        redirection_url: config.redirection_url || "",
        redirection_delay_seconds: config.redirection_delay_seconds ?? 5,
        auto_redirect: config.auto_redirect ?? false,
        shareable_info: config.shareable_info || [],
        redirect_triggering_categories: config.redirect_triggering_categories || []
      }));
    }

    static mapResponseFilters(filters = []) {
      if (!Array.isArray(filters)) return [];

      return filters.map(filter => ({
        pattern: filter.pattern || "",
        type: filter.type || "plain",
        is_case_sensitive: filter.is_case_sensitive ?? false,
        replace_with: filter.replace_with ?? ""
      }));
    }

    static mapQuestionnaireConfig(configs = []) {
      if (!Array.isArray(configs)) return [];
      
    return configs.map(config => ({
      questionnaire_id: config.questionnaire_id || "",
      display_rule: config.display_rule || "",
      frequency: config.frequency || {},
      triggers: config.triggers || [],
      order: config.order || 1,
      keywords: config.keywords || [],
      use_intelligent_detection: config.use_intelligent_detection ?? false,
      active: config.active ?? true,
      intro_message: config.intro_message || "",
      completion_message: config.completion_message || ""
    }));
  }

  static mapLiffEndpointUrlConfig(config) {
    if (!config || typeof config !== "object") {
      return {
        liff_id: "",
        group_invitation_token: "",
        base_url: ""
      };
    }

    return {
      liff_id: config.liff_id || "",
      group_invitation_token: config.group_invitation_token || "",
      base_url: config.base_url || ""
    };
  }

  static mapSessionCloseTriggerConfig(config) {
    if (!config || typeof config !== "object") {
      return {
        is_enabled: false,
        triggers: []
      };
    }

    const is_enabled = config.is_enabled ?? false;
    return {
      is_enabled,
      triggers: is_enabled ? (config.triggers || []) : []
    };
  }

  static mapTimeSeriesConfig(config) {
    if (!config || typeof config !== "object") {
      return {
        require_user_confirmation: false,
        show_reference_fields: false,
        default_timezone: null
      };
    }

    return {
      require_user_confirmation: config.require_user_confirmation ?? false,
      show_reference_fields: config.show_reference_fields ?? false,
      // A cleared picker gives "", the backend wants null.
      default_timezone: config.default_timezone || null
    };
  }

  static mapBackendToFrontend(
    profileData = _.cloneDeep(GPT_PROFILE_INIT_DATA)
  ) {
    return {
      profileName: profileData?.name,
      intro: profileData?.intro,
      system: profileData.system,
      instructions: profileData?.instructions || "",
      model: profileData?.model,
      provider: profileData?.provider || "",
      temperature: profileData?.temperature,
      top_p: profileData?.top_p,
      top_k: profileData?.top_k,
      frequencyPenalty: profileData?.frequency_penalty,
      isAutoEvaluation: profileData?.is_auto_evaluation,
      presencePenalty: profileData?.presence_penalty,
      stop: profileData?.stop,
      responseFormat: profileData.response_format,
      searchMaxToken: profileData?.search_max_token,
      completionToken: profileData?.completion_token,
      visionSettings: {
        resolution: profileData.vision_settings?.resolution,
        imageContextEnabled:
        profileData.vision_settings?.is_image_context_enabled,
        isConversationLearning:
        profileData.vision_settings?.is_conversation_learning || false,
      },
      isConversationAdding: profileData?.is_conversation_adding || false,
      isBackgroundAsk: profileData?.is_background_ask ?? false,
      id: profileData?.id,
      access_tags: profileData?.access_tags || [],
      datasetTags: {
        includes: profileData.dataset_tags?.includes || [],
        excludes: profileData.dataset_tags?.excludes || [],
      },
      chat_history_length: profileData?.chat_history_length,
      is_personalizer_only: profileData?.is_personalizer_only,
      is_auto_evaluation: profileData?.is_auto_evaluation,
      search_mode: profileData?.search_mode,
      introToken: profileData?.intro_token,
      systemToken: profileData?.system_token,
      dataset_languages: profileData?.dataset_languages || [],
      is_lang_auto_detect: profileData.is_lang_auto_detect,
      content_moderation: GptProfileMapper.mapContentModeration(profileData?.content_moderation || {}),
      external_system_configs: GptProfileMapper.mapExternalSystemConfig(profileData?.external_system_configs || []),
      response_filters: GptProfileMapper.mapResponseFilters(profileData?.response_filters || []),
      questionnaire_config: GptProfileMapper.mapQuestionnaireConfig(profileData?.questionnaire_config  || []),
      office_hour_start: profileData?.office_hour_start,
      office_hour_end: profileData?.office_hour_end,
      out_of_office_message: profileData?.out_of_office_message,
      max_concurrent_sessions: profileData?.max_concurrent_sessions,
      session_limit_reached_message: profileData?.session_limit_reached_message,
      session_already_finished_message: profileData?.session_already_finished_message,
      session_timeout_minutes: profileData?.session_timeout_minutes,
      tools: profileData?.tools || [],
      is_citation_enabled: profileData?.is_citation_enabled,
      liff_endpoint_url_config: GptProfileMapper.mapLiffEndpointUrlConfig(profileData?.liff_endpoint_url_config),
      selected_profile_tags: [],
      liff_endpoint_url: profileData?.liff_endpoint_url || "",
      session_close_trigger_config: GptProfileMapper.mapSessionCloseTriggerConfig(profileData?.session_close_trigger_config),
      time_series_config: GptProfileMapper.mapTimeSeriesConfig(profileData?.time_series_config),
    };
  }

  static mapFrontendToBackend(profileData) {
    const dataset_tags = {}
    let includeDatasetTags = false
    if(profileData.datasetTags.includes.length > 0){
      dataset_tags["includes"] = profileData.datasetTags.includes
      includeDatasetTags = true
    }
    if(profileData.datasetTags.excludes.length > 0){
      dataset_tags["excludes"] = profileData.datasetTags.excludes
      includeDatasetTags = true
    }

    const isUpdate = Boolean(profileData.id);

    const liffConfig = profileData.liff_endpoint_url_config;

    const hasLiffId =
      liffConfig?.liff_id && String(liffConfig.liff_id).trim() !== '';

    const hasGroupToken =
      liffConfig?.group_invitation_token &&
      String(liffConfig.group_invitation_token).trim() !== '';

    return {
      name: profileData.profileName,
      intro: profileData.intro,
      system: profileData.system,
      instructions: profileData.instructions || "",
      model: profileData.model,
      provider: profileData.provider || "",
      temperature: profileData.temperature ? parseFloat(profileData.temperature) : 0,
      top_p: validations.isEmpty(profileData.top_p) ? 1 : parseFloat(profileData.top_p),
      top_k: profileData.top_k ? parseFloat(profileData.top_k) : 20,
      frequency_penalty: profileData.frequencyPenalty
        ? parseFloat(profileData.frequencyPenalty)
        : 0,
      presence_penalty: profileData.presencePenalty
        ? parseFloat(profileData.presencePenalty)
        : 0,
      stop: profileData.stop,
      response_format: profileData.responseFormat,
      search_max_token: (profileData.searchMaxToken !== undefined && profileData.searchMaxToken !== null)
        ? parseInt(profileData.searchMaxToken)
        : 2500,
      completion_token: profileData.completionToken
        ? parseInt(profileData.completionToken)
        : 2000,
      vision_settings: {
        resolution: profileData.visionSettings.resolution,
        is_image_context_enabled: profileData.visionSettings.imageContextEnabled,
        is_conversation_learning: profileData.visionSettings.isConversationLearning || false,
      },
      is_conversation_adding: profileData.isConversationAdding || false,
      is_background_ask: profileData.isBackgroundAsk ?? false,
      id: profileData.id,
      access_tags: profileData.access_tags,
      dataset_tags: includeDatasetTags ? dataset_tags : null,
      chat_history_length: profileData.chat_history_length ? parseFloat(profileData.chat_history_length) : 0,
      is_personalizer_only: profileData.is_personalizer_only ? profileData.is_personalizer_only : false,
      is_auto_evaluation: profileData.is_auto_evaluation ? profileData.is_auto_evaluation : false,
      search_mode: profileData.search_mode,
      intro_token: profileData.introToken,
      system_token: profileData.systemToken,
      dataset_languages: profileData.dataset_languages || [],
      ...(profileData.is_lang_auto_detect != null && {
        is_lang_auto_detect: profileData.is_lang_auto_detect
      }),
      content_moderation: GptProfileMapper.mapContentModeration(profileData?.content_moderation || {}),
      external_system_configs: GptProfileMapper.mapExternalSystemConfig(profileData?.external_system_configs || []),
      response_filters: GptProfileMapper.mapResponseFilters(profileData?.response_filters || []),
      questionnaire_config: GptProfileMapper.mapQuestionnaireConfig(profileData.questionnaire_config  || []),
      office_hour_start: profileData.office_hour_start ?  profileData.office_hour_start : null,
      office_hour_end: profileData.office_hour_end ? profileData.office_hour_end : null,
      out_of_office_message: profileData.out_of_office_message ? profileData.out_of_office_message : null,
      max_concurrent_sessions: profileData.max_concurrent_sessions || null,
      session_limit_reached_message: profileData.session_limit_reached_message ? profileData.session_limit_reached_message : null,
      session_already_finished_message: profileData.session_already_finished_message ? profileData.session_already_finished_message : null,
      session_timeout_minutes: profileData.session_timeout_minutes || null,
      tools: profileData?.tools || [],
      is_citation_enabled: !!profileData.is_citation_enabled,
      ...(isUpdate
        ? {
            liff_endpoint_url_config: hasLiffId || hasGroupToken
              ? GptProfileMapper.mapLiffEndpointUrlConfig(liffConfig)
              : null
          }
        : hasLiffId || hasGroupToken
          ? {
              liff_endpoint_url_config:
                GptProfileMapper.mapLiffEndpointUrlConfig(liffConfig),
            }
          : {}
      ),
      liff_endpoint_url: profileData?.liff_endpoint_url || "",
      session_close_trigger_config: GptProfileMapper.mapSessionCloseTriggerConfig(profileData?.session_close_trigger_config),
      time_series_config: GptProfileMapper.mapTimeSeriesConfig(profileData?.time_series_config),
    };
  }
}
